import React, { useEffect, useState } from 'react';
import { ArrowLeft, Loader2, Sparkles, FileText } from 'lucide-react';
import { toast } from 'react-toastify';
import { workflowsApi, Workflow, WorkflowTemplate } from '../../../../services/workflowsApi';

interface WorkflowTemplateGalleryProps {
  clientId: string;
  onCancel: () => void;
  onCreated: (wf: Workflow) => void;
}

export const WorkflowTemplateGallery: React.FC<WorkflowTemplateGalleryProps> = ({
  clientId,
  onCancel,
  onCreated,
}) => {
  const [templates, setTemplates] = useState<WorkflowTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [creatingId, setCreatingId] = useState<string | null>(null);

  useEffect(() => {
    (async () => {
      try {
        const items = await workflowsApi.listTemplates();
        setTemplates(items);
      } catch (err: any) {
        toast.error(err?.response?.data?.detail || 'Failed to load templates');
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const cloneTemplate = async (tpl: WorkflowTemplate) => {
    setCreatingId(tpl.id);
    try {
      const wf = await workflowsApi.create(clientId, {
        name: tpl.name,
        description: tpl.description,
        channel: (tpl.channel as any) || 'whatsapp',
        trigger: tpl.trigger,
        definition: tpl.definition,
        active: false,
      });
      toast.success(`"${wf.name}" created — customize and activate when ready`);
      onCreated(wf);
    } catch (err: any) {
      toast.error(err?.response?.data?.detail || 'Failed to create workflow');
    } finally {
      setCreatingId(null);
    }
  };

  const cloneBlank = async () => {
    setCreatingId('blank');
    try {
      const wf = await workflowsApi.create(clientId, {
        name: 'Untitled workflow',
        channel: 'whatsapp',
        trigger: { type: 'manual' },
        definition: {
          steps: [
            { id: 's1', type: 'ask', prompt: 'Question 1', response_type: 'text', timeout_hours: 6 } as any,
            { id: 'end', type: 'finish' } as any,
          ],
        },
        active: false,
      });
      toast.success('Blank workflow created');
      onCreated(wf);
    } catch (err: any) {
      toast.error(err?.response?.data?.detail || 'Failed to create workflow');
    } finally {
      setCreatingId(null);
    }
  };

  return (
    <div className="space-y-4">
      <button
        onClick={onCancel}
        className="inline-flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-800"
      >
        <ArrowLeft size={14} /> Back to workflows
      </button>

      <div>
        <h4 className="font-medium text-gray-900">Choose a starter template</h4>
        <p className="text-xs text-gray-500 mt-0.5">
          Each template is fully editable — change prompts, schedule, audience.
        </p>
      </div>

      {loading ? (
        <div className="bg-white rounded-xl border border-gray-200 p-10 text-center text-sm text-gray-500">
          <Loader2 size={18} className="animate-spin inline-block mr-2" />
          Loading templates…
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {templates.map((tpl) => (
            <button
              key={tpl.id}
              onClick={() => cloneTemplate(tpl)}
              disabled={creatingId !== null}
              className="text-left bg-white rounded-xl border-2 border-gray-200 hover:border-emerald-300 hover:shadow-sm transition-all p-5 disabled:opacity-50"
            >
              <div className="flex items-start gap-3 mb-3">
                <div className="w-10 h-10 rounded-lg bg-emerald-50 flex items-center justify-center shrink-0">
                  <Sparkles size={18} className="text-emerald-600" />
                </div>
                <div className="flex-1 min-w-0">
                  <h5 className="font-medium text-gray-900">{tpl.name}</h5>
                  {tpl.description && (
                    <p className="text-xs text-gray-500 mt-1 leading-relaxed">{tpl.description}</p>
                  )}
                </div>
              </div>
              <div className="flex flex-wrap gap-1.5">
                {(tpl.use_cases || []).map((u) => (
                  <span
                    key={u}
                    className="text-[10px] font-medium bg-gray-100 text-gray-600 px-1.5 py-0.5 rounded"
                  >
                    {u}
                  </span>
                ))}
                <span className="text-[10px] font-medium bg-emerald-50 text-emerald-700 px-1.5 py-0.5 rounded ml-auto">
                  {tpl.definition?.steps?.length ?? 0} steps
                </span>
              </div>
              {creatingId === tpl.id && (
                <div className="mt-3 flex items-center gap-1.5 text-xs text-emerald-600">
                  <Loader2 size={12} className="animate-spin" /> Creating…
                </div>
              )}
            </button>
          ))}

          <button
            onClick={cloneBlank}
            disabled={creatingId !== null}
            className="text-left bg-white rounded-xl border-2 border-dashed border-gray-200 hover:border-gray-400 hover:shadow-sm transition-all p-5 disabled:opacity-50"
          >
            <div className="flex items-start gap-3 mb-3">
              <div className="w-10 h-10 rounded-lg bg-gray-100 flex items-center justify-center shrink-0">
                <FileText size={18} className="text-gray-600" />
              </div>
              <div>
                <h5 className="font-medium text-gray-900">Blank workflow</h5>
                <p className="text-xs text-gray-500 mt-1 leading-relaxed">
                  Start from scratch — add your own steps and triggers.
                </p>
              </div>
            </div>
            {creatingId === 'blank' && (
              <div className="mt-1 flex items-center gap-1.5 text-xs text-gray-500">
                <Loader2 size={12} className="animate-spin" /> Creating…
              </div>
            )}
          </button>
        </div>
      )}
    </div>
  );
};

export default WorkflowTemplateGallery;
